import './timeregistration.css';
import { useEffect, useState } from 'react';
import { Panel } from '../../components/panel/panel';
import InputField from '../../components/input-field/input-field';
import TimeRegistrationTop from '../../components/timeregistration/top/timeregistration-top';
import TimeRegistrationSummary from '../../components/timeregistration/summary/timeregistration-summary';
import TimeRegistrationUserMetrics from '../../components/timeregistration/user-metrics/timeregistration-user-metrics';
import TimeRegistrationDateRangeFilter from '../../components/timeregistration/date-range-filter/timeregistration-date-range-filter';
import { calculateAllUsersMetrics } from './timeregistration-utils';
import Button from '../../components/button/button';
import * as XLSX from 'xlsx';

const TimeRegistrationOverview = ({
  logs,
  companyData,
  dateRange,
  setDateRange,
  employees,
  employeesToDisplay,
  setEmployeesToDisplay,
  valueToDisplay,
  setValueToDisplay,
  employeeToDisplayInFilter,
  setEmployeeToDisplayInFilter,
  showInactive,
  setShowInactive,
  periodType,
  setPeriodType,
}) => {
  const [metrics, setMetrics] = useState({});

  const handleDateRangeChange = (newDateRange) => {
    setDateRange(newDateRange);
  };

  const handleExportToExcel = () => {
    const roundToNearestQuarter = (value) => {
      return Math.round(value * 4) / 4;
    };

    // Get all unique timeOffTypes from companyData
    const timeOffTypes = companyData?.timeOffTypes || [];

    const convertedMetricsForXlsx = Object.keys(metrics).map((id) => {
      const data = metrics[id];
      const firstName = employees.find((employee) => employee.id === id).data
        .firstName;
      const lastName = employees.find((employee) => employee.id === id).data
        .lastName;

      // Create base row data
      const rowData = {
        Medarbejder: `${firstName} ${lastName}`,
        Normtid: Math.max(0, data.normTime),
        Arbejdstid: Math.max(0, data.work?.hours),
        Sygdom: Math.max(0, data.sickLeave?.hours),
        Feriedage: (() => {
          const holidayHours = data.holidays?.hours || 0; // Fixed: holidays (plural)
          const calculatedDays = holidayHours / 7.5;
          const finalValue = Math.max(0, calculatedDays);

          return finalValue;
        })(),
        'Arbejde u.f. normtid': Math.max(0, data.overtime?.hours),
        'Gennemnsnit pr. uge': Math.max(
          0,
          roundToNearestQuarter(data.avgHoursPerWeek)
        ),
      };

      // Add columns for each timeOffType
      timeOffTypes.forEach((timeOffType) => {
        const hours = data.timeOff?.byType?.[timeOffType.value] || 0;
        rowData[`Fri - ${timeOffType.label}`] = Math.max(0, hours);
      });

      // Add total timeOff column (sum of all types)
      rowData['Fri - Total'] = Math.max(0, data.timeOff?.hours || 0);

      return rowData;
    });

    const worksheet = XLSX.utils.json_to_sheet(convertedMetricsForXlsx);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Sheet1');
    const excelBuffer = XLSX.write(workbook, {
      bookType: 'xlsx',
      type: 'array',
    });
    const blob = new Blob([excelBuffer], { type: 'application/octet-stream' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `HRfamly tidsregistreringsoverblik - ${new Date(
      dateRange.startDate
    ).toLocaleDateString('da-DK')}-${new Date(
      dateRange.endDate
    ).toLocaleDateString('da-DK')}.xlsx`;
    document.body.appendChild(link);
    link.click();
    link.remove();

    // Revoke the URL to free up memory
    window.URL.revokeObjectURL(url);
  };

  useEffect(() => {
    const logsByUser = logs.reduce((acc, log) => {
      const { userId } = log;
      if (!acc[userId]) {
        acc[userId] = [];
      }
      acc[userId].push(log);
      return acc;
    }, {});

    const allUsersMetrics = calculateAllUsersMetrics(
      logsByUser,
      employeesToDisplay,
      companyData,
      dateRange
    );
    setMetrics(allUsersMetrics);
  }, [logs, employeesToDisplay, companyData, dateRange]);

  return (
    <Panel
      noPadding
      inlineHeader
      header={
        <TimeRegistrationTop
          title="Overblik"
          filters={
            <>
              <Button
                className="small"
                buttonStyle="text-link"
                onClick={handleExportToExcel}
              >
                Eksporter til XLSX
              </Button>
              <TimeRegistrationDateRangeFilter
                key={'filter-date'}
                name={'date'}
                value={dateRange}
                onChange={handleDateRangeChange}
                valueToDisplay={valueToDisplay}
                setValueToDisplay={setValueToDisplay}
                periodType={periodType}
                setPeriodType={setPeriodType}
              />

              <InputField
                key={'filter-employee'}
                label={'Medarbejder'}
                className={'filter-employee'}
                type="select2"
                name={'employee'}
                isMulti
                options={employees
                  .filter((employee) => {
                    // Filter dropdown options based on showInactive state
                    if (showInactive) {
                      return (
                        employee?.data?.employmentStatus === 'inactive' &&
                        employee?.data?.endDate
                      );
                    } else {
                      return employee?.data?.employmentStatus !== 'inactive';
                    }
                  })
                  .map((employee) => ({
                    value: employee.id,
                    label: `${employee.data.firstName} ${employee.data.lastName}`,
                    target: {
                      value: employee.id,
                      name: 'employee',
                    },
                  }))}
                placeholder="- Vælg -"
                value={
                  Array.isArray(employeeToDisplayInFilter) &&
                  employeeToDisplayInFilter.length > 0
                    ? employeeToDisplayInFilter.map((emp) => ({
                        value: emp.id || emp.value,
                        label: emp.data
                          ? `${emp.data.firstName} ${emp.data.lastName}`
                          : emp.label,
                        target: {
                          value: emp.id || emp.value,
                          name: 'employee',
                        },
                      }))
                    : []
                }
                onChange={(selectedOptions) => {
                  if (
                    Array.isArray(selectedOptions) &&
                    selectedOptions.length > 0
                  ) {
                    const selectedEmployees = selectedOptions
                      .map((option) =>
                        employees.find(
                          (employee) => employee.id === option.value
                        )
                      )
                      .filter(Boolean);
                    setEmployeesToDisplay(selectedEmployees);
                    setEmployeeToDisplayInFilter(selectedEmployees);
                  } else {
                    // Clear the employee filter - show all employees (filtered by active/inactive status)
                    const filteredEmployees = employees.filter((employee) => {
                      if (showInactive) {
                        return (
                          employee?.data?.employmentStatus === 'inactive' &&
                          employee?.data?.endDate
                        );
                      } else {
                        return employee?.data?.employmentStatus !== 'inactive';
                      }
                    });
                    setEmployeesToDisplay(filteredEmployees);
                    setEmployeeToDisplayInFilter([]);
                  }
                }}
              />
              <InputField
                type="checkbox"
                label={'Vis inaktive'}
                name={'showInactive'}
                onChange={(e) => {
                  const checked = e.target ? e.target.checked : e;
                  setShowInactive(checked);
                  // Reset employee filter when switching between active/inactive
                  const filteredEmployees = employees.filter((employee) => {
                    if (checked) {
                      return (
                        employee?.data?.employmentStatus === 'inactive' &&
                        employee?.data?.endDate
                      );
                    } else {
                      return employee?.data?.employmentStatus !== 'inactive';
                    }
                  });
                  setEmployeesToDisplay(filteredEmployees);
                  setEmployeeToDisplayInFilter([]);
                }}
                checked={showInactive}
              />
            </>
          }
        />
      }
    >
      <TimeRegistrationSummary metrics={metrics} />
      <TimeRegistrationUserMetrics
        companyData={companyData}
        employees={employeesToDisplay}
        metrics={metrics}
      />
    </Panel>
  );
};

export default TimeRegistrationOverview;
